import { useEffect, useMemo, useState } from 'react';
import { Input, Tag, Button, Empty, Checkbox, Spin } from 'antd';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';

const API = process.env.REACT_APP_API_URL;

/**
 * Props
 * - value: string[]        codes NAF sélectionnés (format "47.11D")
 * - onChange: (codes) => void
 * - disabled?: boolean
 */
export default function NafPicker({ value = [], onChange, disabled = false }) {
  const [sections, setSections] = useState([]);
  const [allCodes, setAllCodes] = useState([]); // [[code, label, section, divCode, divLabel]]
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeSection, setActiveSection] = useState(null);

  useEffect(() => {
    fetch(`${API}/naf/subclasses`)
      .then(r => r.json())
      .then(({ sections: s, codes: c }) => {
        setSections(s);
        setAllCodes(c);
        if (s.length > 0) setActiveSection(s[0].code);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const valueSet = useMemo(() => new Set(value), [value]);

  const normalize = str =>
    str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();

  // Codes filtrés par section ET/OU recherche
  const filtered = useMemo(() => {
    const q = normalize(search.trim());
    if (q) {
      return allCodes.filter(
        ([code, label]) =>
          normalize(code).includes(q) || normalize(label).includes(q)
      );
    }
    if (activeSection) {
      return allCodes.filter(([, , section]) => section === activeSection);
    }
    return allCodes;
  }, [allCodes, activeSection, search]);

  // Grouper par division pour affichage
  const byDivision = useMemo(() => {
    const groups = [];
    let currentDiv = null;
    filtered.forEach(row => {
      const [, , , divCode, divLabel] = row;
      if (divCode !== currentDiv) {
        currentDiv = divCode;
        groups.push({ divCode, divLabel, rows: [] });
      }
      groups[groups.length - 1].rows.push(row);
    });
    return groups;
  }, [filtered]);

  const toggle = code => {
    const next = new Set(valueSet);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    onChange(Array.from(next));
  };

  const toggleDivision = (rows, checked) => {
    const next = new Set(valueSet);
    rows.forEach(([code]) => {
      if (checked) next.add(code);
      else next.delete(code);
    });
    onChange(Array.from(next));
  };

  const toggleSection = (sectionCode, checked) => {
    const sectionCodes = allCodes
      .filter(([, , s]) => s === sectionCode)
      .map(([c]) => c);
    const next = new Set(valueSet);
    sectionCodes.forEach(c => {
      if (checked) next.add(c);
      else next.delete(c);
    });
    onChange(Array.from(next));
  };

  const sectionCount = sCode =>
    allCodes.filter(([c, , s]) => s === sCode && valueSet.has(c)).length;

  const divChecked = rows => rows.every(([c]) => valueSet.has(c));
  const divPartial = rows =>
    rows.some(([c]) => valueSet.has(c)) && !divChecked(rows);

  const isSearching = search.trim().length > 0;

  if (loading)
    return <Spin style={{ display: 'block', margin: '40px auto' }} />;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        height: '100%',
      }}
    >
      {/* Search */}
      <Input
        prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
        placeholder="Rechercher par libellé ou code APE (ex: 47.11D, boulangerie, coiffure…)"
        value={search}
        onChange={e => setSearch(e.target.value)}
        allowClear
        disabled={disabled}
        size="large"
      />

      {/* Body : 2 colonnes */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          gap: 0,
          border: '1px solid #e2e8f0',
          borderRadius: 8,
          overflow: 'hidden',
        }}
      >
        {/* Colonne gauche — sections */}
        {!isSearching && (
          <div
            style={{
              width: 210,
              flexShrink: 0,
              overflowY: 'auto',
              borderRight: '1px solid #e2e8f0',
              background: '#f8fafc',
            }}
          >
            {sections.map(sec => {
              const n = sectionCount(sec.code);
              const active = activeSection === sec.code;
              return (
                <div
                  key={sec.code}
                  onClick={() => setActiveSection(sec.code)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '9px 12px',
                    cursor: 'pointer',
                    background: active ? '#eff6ff' : 'transparent',
                    borderLeft: active
                      ? '3px solid #3b82f6'
                      : '3px solid transparent',
                    borderBottom: '1px solid #f1f5f9',
                    transition: 'background 0.1s',
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      fontFamily: 'monospace',
                      color: active ? '#1d4ed8' : '#64748b',
                      background: active ? '#dbeafe' : '#e2e8f0',
                      padding: '1px 5px',
                      borderRadius: 3,
                      flexShrink: 0,
                    }}
                  >
                    {sec.code}
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      color: active ? '#1e293b' : '#475569',
                      fontWeight: active ? 600 : 400,
                      lineHeight: '1.3',
                      flex: 1,
                    }}
                  >
                    {sec.label}
                  </span>
                  {n > 0 && (
                    <span
                      style={{
                        flexShrink: 0,
                        fontSize: 10,
                        fontWeight: 700,
                        background: '#3b82f6',
                        color: '#fff',
                        borderRadius: 999,
                        padding: '0 5px',
                        minWidth: 18,
                        textAlign: 'center',
                      }}
                    >
                      {n}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Colonne droite — codes */}
        <div style={{ flex: 1, overflowY: 'auto', background: '#fff' }}>
          {/* Header de section */}
          {!isSearching && activeSection && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 14px',
                borderBottom: '1px solid #e2e8f0',
                background: '#f8fafc',
                position: 'sticky',
                top: 0,
                zIndex: 1,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                {sections.find(s => s.code === activeSection)?.label}
                <span
                  style={{
                    color: '#94a3b8',
                    fontWeight: 400,
                    marginLeft: 8,
                    fontSize: 12,
                  }}
                >
                  {filtered.length} codes
                </span>
              </span>
              <div style={{ display: 'flex', gap: 6 }}>
                <Button
                  size="small"
                  onClick={() => toggleSection(activeSection, true)}
                  disabled={disabled}
                >
                  Tout sélectionner
                </Button>
                <Button
                  size="small"
                  onClick={() => toggleSection(activeSection, false)}
                  disabled={disabled}
                >
                  Tout vider
                </Button>
              </div>
            </div>
          )}

          {isSearching && (
            <div
              style={{
                padding: '8px 14px',
                borderBottom: '1px solid #e2e8f0',
                background: '#fffbeb',
                fontSize: 12,
                color: '#92400e',
              }}
            >
              {filtered.length} résultat{filtered.length > 1 ? 's' : ''} pour «{' '}
              {search} »
            </div>
          )}

          {byDivision.length === 0 && (
            <div style={{ padding: 40 }}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Aucun résultat"
              />
            </div>
          )}

          {byDivision.map(({ divCode, divLabel, rows }) => {
            const checked = divChecked(rows);
            const partial = divPartial(rows);
            return (
              <div key={divCode}>
                {/* En-tête de division */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 14px',
                    background: '#f1f5f9',
                    borderBottom: '1px solid #e2e8f0',
                    cursor: 'pointer',
                  }}
                  onClick={() => !disabled && toggleDivision(rows, !checked)}
                >
                  <Checkbox
                    checked={checked}
                    indeterminate={partial}
                    onChange={e => {
                      e.stopPropagation();
                      toggleDivision(rows, e.target.checked);
                    }}
                    disabled={disabled}
                  />
                  <span
                    style={{
                      fontFamily: 'monospace',
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#64748b',
                    }}
                  >
                    {divCode}
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: '#374151',
                      flex: 1,
                    }}
                  >
                    {divLabel}
                  </span>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>
                    {rows.filter(([c]) => valueSet.has(c)).length}/{rows.length}
                  </span>
                </div>

                {/* Lignes de codes */}
                {rows.map(([code, label]) => {
                  const sel = valueSet.has(code);
                  const hl =
                    isSearching && normalize(code).includes(normalize(search));
                  return (
                    <div
                      key={code}
                      onClick={() => !disabled && toggle(code)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '7px 14px 7px 28px',
                        borderBottom: '1px solid #f8fafc',
                        cursor: disabled ? 'default' : 'pointer',
                        background: sel ? '#eff6ff' : 'transparent',
                        transition: 'background 0.1s',
                      }}
                    >
                      <Checkbox
                        checked={sel}
                        onChange={e => {
                          e.stopPropagation();
                          toggle(code);
                        }}
                        disabled={disabled}
                        style={{ flexShrink: 0 }}
                      />
                      <span
                        style={{
                          fontFamily: 'monospace',
                          fontSize: 12,
                          fontWeight: 700,
                          padding: '1px 7px',
                          borderRadius: 4,
                          background: sel
                            ? '#dbeafe'
                            : hl
                              ? '#fef3c7'
                              : '#f1f5f9',
                          color: sel ? '#1d4ed8' : hl ? '#92400e' : '#475569',
                          border: sel
                            ? '1px solid #93c5fd'
                            : hl
                              ? '1px solid #fcd34d'
                              : '1px solid #e2e8f0',
                          flexShrink: 0,
                        }}
                      >
                        {code}
                      </span>
                      <span
                        style={{
                          fontSize: 13,
                          color: sel ? '#1e293b' : '#374151',
                          fontWeight: sel ? 500 : 400,
                          flex: 1,
                        }}
                      >
                        {label}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Strip de sélection */}
      {value.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            background: '#eff6ff',
            borderRadius: 8,
            border: '1px solid #bfdbfe',
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: '#1d4ed8',
              flexShrink: 0,
            }}
          >
            {value.length} code{value.length > 1 ? 's' : ''} sélectionné
            {value.length > 1 ? 's' : ''} :
          </span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, flex: 1 }}>
            {value.slice(0, 40).map(code => (
              <Tag
                key={code}
                closable
                onClose={() => toggle(code)}
                style={{
                  fontFamily: 'monospace',
                  fontSize: 11,
                  fontWeight: 600,
                  margin: 0,
                  background: '#dbeafe',
                  borderColor: '#93c5fd',
                  color: '#1d4ed8',
                }}
              >
                {code}
              </Tag>
            ))}
            {value.length > 40 && (
              <Tag style={{ margin: 0, color: '#64748b' }}>
                +{value.length - 40} autres
              </Tag>
            )}
          </div>
          <Button
            size="small"
            danger
            icon={<CloseOutlined />}
            onClick={() => onChange([])}
          >
            Tout vider
          </Button>
        </div>
      )}
    </div>
  );
}
